<template>
  <div ref="wrapperRef" class="videojs-wrapper">
    <video ref="videoPlayer" class="video-js vjs-default-skin"></video>
    <div
      v-if="morePanelOpen"
      class="vjs-more-panel"
      role="menu"
      @click.stop
    >
      <div class="vjs-more-panel__section">
        <div class="vjs-more-panel__title">再生速度</div>
        <ul class="vjs-more-panel__list">
          <li
            v-for="rate in playbackRates"
            :key="`rate-${rate}`"
            class="vjs-more-panel__item"
            :class="{ 'vjs-more-panel__item--selected': selectedRate === rate }"
            role="menuitemradio"
            :aria-checked="selectedRate === rate"
            @click="selectRate(rate)"
          >
            <i class="fas fa-check vjs-more-panel__item-check" aria-hidden="true"></i>
            <span>{{ rate === 1 ? '通常 (1x)' : rate + 'x' }}</span>
          </li>
        </ul>
      </div>
      <div v-if="audioTracks.length > 1" class="vjs-more-panel__section">
        <div class="vjs-more-panel__title">音声</div>
        <ul class="vjs-more-panel__list">
          <li
            v-for="track in audioTracks"
            :key="`track-${track.index}`"
            class="vjs-more-panel__item"
            :class="{ 'vjs-more-panel__item--selected': selectedTrackIndex === track.index }"
            role="menuitemradio"
            :aria-checked="selectedTrackIndex === track.index"
            @click="selectAudioTrack(track.index)"
          >
            <i class="fas fa-check vjs-more-panel__item-check" aria-hidden="true"></i>
            <span>{{ track.label }}</span>
          </li>
        </ul>
      </div>
      <div v-if="hasTimecode" class="vjs-more-panel__section">
        <div class="vjs-more-panel__title">タイムコード</div>
        <ul class="vjs-more-panel__list">
          <li
            class="vjs-more-panel__item"
            :class="{ 'vjs-more-panel__item--selected': timecodeActive }"
            role="menuitemcheckbox"
            :aria-checked="timecodeActive"
            @click="toggleTimecode()"
          >
            <i class="fas fa-check vjs-more-panel__item-check" aria-hidden="true"></i>
            <span>タイムコード表示</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import { computeCurrentTimecode } from '../lib/timecode'
import { registerTimecodeComponents, bindSeekBarTimecodeTooltip } from '../lib/videojsTimecodeControls'

const props = defineProps({
  poster: String,
  src: String,
  aspectRatio: {
    type: String,
    default: null
  },
  compact: {
    type: Boolean,
    default: false
  },
  startTimecode: {
    type: String,
    default: null
  },
  frameRate: {
    type: [String, Number],
    default: null
  },
  clipOffsetSeconds: {
    type: Number,
    default: 0
  }
})

const player = ref(null)
const videoPlayer = ref(null)
const wrapperRef = ref(null)

const audioTracks = ref([])
const selectedTrackIndex = ref(-1)
let audioTrackList = null

const playbackRates = [0.5, 1, 1.5, 2]
const selectedRate = ref(1)

const morePanelOpen = ref(false)
const isFullscreen = ref(false)
const playerReady = ref(false)
const metadataReady = ref(false)
const pendingSeekSeconds = ref(null)

const isCompactMode = computed(() => props.compact && !isFullscreen.value)
const hasTimecode = computed(() => !!(props.startTimecode && props.frameRate))
const timecodeActive = ref(false)
let timecodeToggleComponent = null
let timecodeDisplayComponent = null

const normalizeSeekSeconds = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '' || typeof seconds === 'boolean') {
    return null
  }

  const normalized = Number(seconds)
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : null
}

const applyPendingSeek = () => {
  if (!player.value || !playerReady.value || !metadataReady.value || pendingSeekSeconds.value === null) {
    return
  }

  try {
    player.value.currentTime(pendingSeekSeconds.value)
    pendingSeekSeconds.value = null
  } catch (error) {
    // Keep the reservation for a later player event if the media is not seekable yet.
  }
}

const normalizeAspectRatio = (width, height) => {
  const normalizedWidth = Number(width)
  const normalizedHeight = Number(height)
  if (!Number.isFinite(normalizedWidth) || !Number.isFinite(normalizedHeight)) {
    return null
  }
  if (normalizedWidth <= 0 || normalizedHeight <= 0) {
    return null
  }
  return `${Math.round(normalizedWidth)}:${Math.round(normalizedHeight)}`
}

const applyAspectRatio = (ratio) => {
  if (!player.value || typeof ratio !== 'string' || !ratio.includes(':')) {
    return
  }
  try {
    player.value.aspectRatio(ratio)
  } catch (error) {
    // ignore invalid ratio values
  }
}

const resolvePosterAspectRatio = () =>
  new Promise((resolve) => {
    if (!props.poster) {
      resolve(null)
      return
    }

    const posterImage = new Image()
    posterImage.onload = () => {
      resolve(
        normalizeAspectRatio(posterImage.naturalWidth, posterImage.naturalHeight)
      )
    }
    posterImage.onerror = () => resolve(null)
    posterImage.src = props.poster
  })

const syncAspectRatioFromMetadata = async () => {
  if (!player.value) {
    return
  }

  const techElement = player.value.el()?.querySelector('video')
  const streamAspectRatio = normalizeAspectRatio(
    techElement?.videoWidth,
    techElement?.videoHeight
  )

  if (streamAspectRatio) {
    applyAspectRatio(streamAspectRatio)
    return
  }

  const posterAspectRatio = await resolvePosterAspectRatio()
  if (posterAspectRatio) {
    applyAspectRatio(posterAspectRatio)
  }
}

const togglePanel = () => {
  morePanelOpen.value = !morePanelOpen.value
}

const closePanel = () => {
  morePanelOpen.value = false
}

const selectRate = (rate) => {
  selectedRate.value = rate
  if (player.value) {
    player.value.playbackRate(rate)
  }
  closePanel()
}

const syncAudioTracks = () => {
  if (!audioTrackList) return
  const list = []
  for (let i = 0; i < audioTrackList.length; i++) {
    list.push({
      label: audioTrackList[i].label || `Track ${i + 1}`,
      index: i
    })
    if (audioTrackList[i].enabled) {
      selectedTrackIndex.value = i
    }
  }
  audioTracks.value = list
}

const selectAudioTrack = (index) => {
  if (!player.value) return
  const vjsTracks = player.value.audioTracks()
  for (let i = 0; i < vjsTracks.length; i++) {
    vjsTracks[i].enabled = (i === index)
  }
  selectedTrackIndex.value = index
  closePanel()
}

const toggleTimecode = () => {
  timecodeActive.value = !timecodeActive.value
  closePanel()
}

const handleOutsideClick = (event) => {
  if (!morePanelOpen.value) return
  if (!wrapperRef.value) return
  if (!wrapperRef.value.contains(event.target)) {
    closePanel()
  }
}

const updateCompactClass = () => {
  if (!player.value) return
  const el = player.value.el()
  if (!el) return
  if (isCompactMode.value) {
    el.classList.add('vjs-mode-compact')
  } else {
    el.classList.remove('vjs-mode-compact')
    closePanel()
  }
}

const registerMoreButton = () => {
  if (videojs.getComponent('MoreButton')) {
    return
  }
  const Button = videojs.getComponent('Button')
  class MoreButton extends Button {
    constructor(parent, options) {
      super(parent, options)
      this.controlText('設定')
      this.onToggle = typeof options?.onToggle === 'function' ? options.onToggle : () => {}
    }
    buildCSSClass() {
      return `vjs-more-button vjs-icon-cog ${super.buildCSSClass()}`
    }
    handleClick() {
      this.onToggle()
    }
  }
  videojs.registerComponent('MoreButton', MoreButton)
}

const updateTimecodeDisplay = () => {
  if (!timecodeDisplayComponent || !hasTimecode.value || !player.value) {
    return
  }
  const currentSeconds = player.value.currentTime() || 0
  const timecodeText = computeCurrentTimecode(
    props.startTimecode,
    props.frameRate,
    props.clipOffsetSeconds + currentSeconds
  )
  timecodeDisplayComponent.setTimecodeText(timecodeText)
}

const bindTimecodeControls = () => {
  const controlBar = player.value?.getChild('ControlBar')
  if (!controlBar || timecodeToggleComponent) {
    return
  }

  const displayChildNames = controlBar.children().map((c) => c.name())
  const displayFsIndex = displayChildNames.indexOf('FullscreenToggle')
  const displayInsertIndex = displayFsIndex >= 0 ? displayFsIndex : displayChildNames.length
  timecodeDisplayComponent = controlBar.addChild('TimecodeDisplay', {}, displayInsertIndex)

  const toggleChildNames = controlBar.children().map((c) => c.name())
  const toggleFsIndex = toggleChildNames.indexOf('FullscreenToggle')
  const toggleInsertIndex = toggleFsIndex >= 0 ? toggleFsIndex : toggleChildNames.length
  timecodeToggleComponent = controlBar.addChild(
    'TimecodeToggleButton',
    { onToggle: () => { timecodeActive.value = !timecodeActive.value } },
    toggleInsertIndex
  )

  bindSeekBarTimecodeTooltip(player.value, () => ({
    startTimecode: props.startTimecode,
    frameRate: props.frameRate,
    clipOffsetSeconds: props.clipOffsetSeconds
  }))
}

const unbindTimecodeControls = () => {
  const controlBar = player.value?.getChild('ControlBar')
  if (controlBar && timecodeToggleComponent) {
    controlBar.removeChild(timecodeToggleComponent)
    timecodeToggleComponent.dispose()
  }
  timecodeToggleComponent = null
  if (controlBar && timecodeDisplayComponent) {
    controlBar.removeChild(timecodeDisplayComponent)
    timecodeDisplayComponent.dispose()
  }
  timecodeDisplayComponent = null
  timecodeActive.value = false
}

const syncTimecodeControls = () => {
  if (!player.value) {
    return
  }
  if (hasTimecode.value) {
    bindTimecodeControls()
    updateTimecodeDisplay()
  } else {
    unbindTimecodeControls()
  }
}

onMounted(() => {
  registerMoreButton()
  registerTimecodeComponents(videojs)

  player.value = videojs(videoPlayer.value, {
    poster: props.poster,
    autoplay: false,
    controls: true,
    fluid: true,
    playbackRates: playbackRates,
    controlBar: {
      pictureInPictureToggle: false,
      subsCapsButton: false,
      skipButtons: { forward: 10, backward: 10 },
      remainingTimeDisplay: false,
      currentTimeDisplay: true,
      timeDivider: true,
      durationDisplay: true,
      // video.js既定の children 順（playToggle 〜 fullscreenToggle）をベースに、
      // 経過時間/区切り/総時間の表示位置のみ remainingTimeDisplay と同じ
      // 「シークバーの右」に来るよう並び替える。他の子要素は既定を維持する。
      children: [
        'playToggle',
        'skipBackward',
        'skipForward',
        'volumePanel',
        'progressControl',
        'liveDisplay',
        'seekToLive',
        'currentTimeDisplay',
        'timeDivider',
        'durationDisplay',
        'customControlSpacer',
        'playbackRateMenuButton',
        'chaptersButton',
        'descriptionsButton',
        'subsCapsButton',
        'audioTrackButton',
        'pictureInPictureToggle',
        'fullscreenToggle'
      ]
    },
    userActions: {
      hotkeys(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
        event.preventDefault()
        const duration = this.duration() || 0
        const current = this.currentTime() || 0
        const delta = event.key === 'ArrowRight' ? 10 : -10
        this.currentTime(Math.min(Math.max(current + delta, 0), duration))
      }
    },
    sources: [{
      src: props.src,
      type: 'application/x-mpegURL'
    }],
    html5: {
      vhs: {
        withCredentials: true
      }
    }
  })
  // クリックだけではフォーカスされないため、矢印キーシークをプレーヤー要素フォーカス時のみ発火させる
  player.value.el().setAttribute('tabindex', '0')

  const controlBar = player.value.getChild('ControlBar')
  if (controlBar) {
    const childNames = controlBar.children().map((c) => c.name())
    const fsIndex = childNames.indexOf('FullscreenToggle')
    const insertIndex = fsIndex >= 0 ? fsIndex : childNames.length
    controlBar.addChild('MoreButton', { onToggle: togglePanel }, insertIndex)
  }

  applyAspectRatio(props.aspectRatio)
  syncTimecodeControls()

  player.value.ready(() => {
    playerReady.value = true
    applyPendingSeek()
  })

  player.value.on('timeupdate', updateTimecodeDisplay)
  player.value.on('seeked', updateTimecodeDisplay)

  player.value.on('loadedmetadata', async () => {
    metadataReady.value = true
    await syncAspectRatioFromMetadata()
    applyPendingSeek()
    updateTimecodeDisplay()

    const vjsTracks = player.value.audioTracks()
    audioTrackList = vjsTracks
    syncAudioTracks()
    vjsTracks.addEventListener('addtrack', syncAudioTracks)
    vjsTracks.addEventListener('removetrack', syncAudioTracks)

    vjsTracks.addEventListener('change', () => {
      for (let i = 0; i < vjsTracks.length; i++) {
        if (vjsTracks[i].enabled) {
          selectedTrackIndex.value = i
        }
      }
    })
  })

  player.value.on('ratechange', () => {
    if (player.value) {
      selectedRate.value = player.value.playbackRate()
    }
  })

  player.value.on('fullscreenchange', () => {
    if (player.value) {
      isFullscreen.value = player.value.isFullscreen()
    }
  })

  player.value.on('play', () => {
    closePanel()
  })

  player.value.on('userinactive', () => {
    closePanel()
  })

  updateCompactClass()

  document.addEventListener('click', handleOutsideClick)
})

watch(
  () => props.aspectRatio,
  (nextAspectRatio) => {
    applyAspectRatio(nextAspectRatio)
  }
)

watch(
  () => props.src,
  () => {
    metadataReady.value = false
    pendingSeekSeconds.value = null
  }
)

watch(
  () => [props.startTimecode, props.frameRate],
  () => {
    timecodeActive.value = false
    syncTimecodeControls()
  }
)

watch(timecodeActive, (isActive) => {
  if (!player.value) {
    return
  }
  if (isActive) {
    player.value.addClass('vjs-timecode-active')
  } else {
    player.value.removeClass('vjs-timecode-active')
  }
  updateTimecodeDisplay()
})

watch(isCompactMode, () => {
  updateCompactClass()
})

defineExpose({
  getCurrentTime: () => player.value?.currentTime() ?? 0,
  seekTo: (seconds) => {
    const normalized = normalizeSeekSeconds(seconds)
    if (normalized === null) {
      return
    }

    pendingSeekSeconds.value = normalized
    applyPendingSeek()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
  pendingSeekSeconds.value = null
  playerReady.value = false
  metadataReady.value = false
  if (audioTrackList) {
    audioTrackList.removeEventListener('addtrack', syncAudioTracks)
    audioTrackList.removeEventListener('removetrack', syncAudioTracks)
  }
  if (player.value) {
    player.value.dispose()
  }
  timecodeToggleComponent = null
  timecodeDisplayComponent = null
})
</script>
